import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Divider,
  useTheme,
  CssBaseline
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';

// Create a specific theme for the login page to enforce Calibri and professional look
const loginTheme = createTheme({
  typography: {
    fontFamily: 'Calibri, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Ensure paper doesn't look too flat
        }
      }
    }
  },
});

/**
 * Login page component
 * Redesigned with a Split-Screen Layout for professional appearance
 * @returns {JSX.Element} Login page
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const logoUrl = `${process.env.PUBLIC_URL}/images/logo.png`;

  return (
    <ThemeProvider theme={loginTheme}>
      <Grid container component="main" sx={{ height: '100vh', overflow: 'hidden' }}>
        <CssBaseline />

        {/* LEFT COMPONENT - branding */}
        {/* LEFT COMPONENT - branding */}
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            // Dark professional overlay: Image + Dark Slate/Blue Gradient
            backgroundImage: `linear-gradient(135deg, rgba(15, 32, 65, 0.9) 0%, rgba(30, 58, 138, 0.7) 100%), url(${process.env.PUBLIC_URL}/images/login3.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            position: 'relative',
            // Hide on extra small screens automatically via xs={false}
            '@media (max-width: 600px)': {
              display: 'none'
            }
          }}
        >
          {/* Content Wrapper for better spacing */}
          <Box sx={{ textAlign: 'center', p: 4, maxWidth: '80%', position: 'relative', zIndex: 1 }}>

            {/* Logo with container to ensure visibility */}
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '24px',
                mb: 4,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }}
            >
              <img
                src={logoUrl}
                alt="Tiya-Inventory Logo"
                style={{
                  height: '100px', // Slightly smaller within container
                  width: '100px',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                letterSpacing: '0.5px',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Tiya-Inventory
            </Typography>

            <Typography
              variant="h5"
              sx={{
                opacity: 0.9,
                fontWeight: 300,
                mb: 6,
                letterSpacing: '0.5px'
              }}
            >
              Business Inventory Management System
            </Typography>

            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)', // Glassmorphism text box
                backdropFilter: 'blur(10px)',
                p: 4,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                "Streamline your operations with our advanced inventory solutions.
                Experience real-time tracking and comprehensive reporting."
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* RIGHT COMPONENT - Login Form */}
        <Grid
          item
          xs={12}
          sm={8}
          md={5}
          component={Paper}
          elevation={6}
          square
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto', // Allow scrolling ONLY within the form side if needed
          }}
        >
          <Box
            sx={{
              my: 'auto', // Vertically center the content
              mx: 4,
              display: 'flex',
              flexDirection: 'column',
              // Add padding to ensure content isn't flush against edges on small screens
              p: { xs: 2, md: 8 },
              minHeight: '100vh', // Ensure it takes full height for centering
              justifyContent: 'center'
            }}
          >
            {/* Mobile-only logo (visible when left panel is hidden) */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 3 }}>
              <img
                src={logoUrl}
                alt="Tiya-Inventory Logo"
                style={{ height: 60, width: 60 }}
              />
            </Box>

            <Box sx={{ width: '100%', maxWidth: '450px', mx: 'auto' }}>

              <LoginForm />

              <Divider sx={{ my: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  New to our platform?
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  py: 1.8,
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  color: '#1565c0',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderWidth: '2px',
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(21, 101, 192, 0.08)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Start Your Free Trial
              </Button>

              <Box sx={{ mt: 5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  © {new Date().getFullYear()} Tiya-Inventory. All rights reserved.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default LoginPage;